import CopilotIconAnimation from '@github-ui/copilot-chat-visuals/CopilotIconAnimation'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {commitPath} from '@github-ui/paths'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {
  CopilotIcon,
  GitCommitIcon,
  GitPullRequestIcon,
  ThumbsdownIcon,
  ThumbsupIcon,
  XIcon,
} from '@primer/octicons-react'
import {Flash, Heading, IconButton, Label, RelativeTime} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import styles from './CopilotCommitRecapPanel.module.css'

const GOOGLE_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSeKJgUAL0ENwW13Qy-QuiihzSfXfs81vzP0Jc5FZX-CCNDJnQ/viewform'

// Size badge component with color coding
const SizeBadge = ({size}: {size: string}) => {
  const getSizeVariant = (sizeValue: string) => {
    switch (sizeValue.toUpperCase()) {
      case 'XS':
        return 'success' // Green
      case 'S':
        return 'success' // Green
      case 'M':
        return 'attention' // Yellow
      case 'L':
        return 'severe' // Orange
      case 'XL':
        return 'danger' // Red
      default:
        return 'secondary' // Gray fallback
    }
  }

  return (
    <Label className="ml-2" variant={getSizeVariant(size)} size="small">
      size: {size}
    </Label>
  )
}

// Simple monogram component
const Monogram = ({initials}: {initials: string}) => <div className={styles.Box}>{initials.toUpperCase()}</div>

// Author avatar component that shows either GitHubAvatar or Monogram
const AuthorAvatar = ({
  login,
  avatarUrl,
  name,
  repoOwner,
  repoName,
  refName,
}: {
  login?: string
  avatarUrl?: string
  name: string
  repoOwner: string
  repoName: string
  refName?: string
}) => {
  const displayName = login || name

  // Generate initials from name (first character of first and last name, or just first two characters)
  const getInitials = (str: string) => {
    const trimmed = str.trim()
    if (!trimmed) return '??' // Fallback for empty names

    const words = trimmed.split(/\s+/).filter(word => word.length > 0)
    if (words.length >= 2) {
      return (words[0]?.charAt(0) || '') + (words[words.length - 1]?.charAt(0) || '')
    }
    return trimmed.charAt(0) + (trimmed.charAt(1) || '')
  }

  const initials = getInitials(displayName)

  if (avatarUrl && login) {
    // Create URL to commits page filtered by this author
    const commitsUrl = `/${encodeURIComponent(repoOwner)}/${encodeURIComponent(repoName)}/commits${
      refName ? `/${encodeURIComponent(refName)}` : ''
    }?author=${encodeURIComponent(login)}`

    return (
      <a href={commitsUrl} target="_blank" rel="noopener noreferrer nofollow" className={styles.avatarLink}>
        <GitHubAvatar src={avatarUrl} size={24} alt={`@${login}`} />
      </a>
    )
  }

  return <Monogram initials={initials} />
}

// Component for rendering author name links
const AuthorNameLink = ({
  author,
  repoOwner,
  repoName,
  refName,
  className,
}: {
  author: {login?: string; name: string}
  repoOwner: string
  repoName: string
  refName?: string
  className?: string
}) => {
  if (author.login) {
    // Create URL to commits page filtered by this author
    const commitsUrl = `/${encodeURIComponent(repoOwner)}/${encodeURIComponent(repoName)}/commits${
      refName ? `/${encodeURIComponent(refName)}` : ''
    }?author=${encodeURIComponent(author.login)}`

    return (
      <a
        href={commitsUrl}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className={`${className} ${styles.authorNameLink}`}
      >
        {author.name}
      </a>
    )
  }

  // If no login, render as plain text
  return <span className={`${className} ${styles.authorNameText}`}>{author.name}</span>
}

// Rich PR link component inspired by LinkedPullRequest
const RichPullRequestLink = ({
  pr,
  repoOwner,
  repoName,
}: {
  pr: {number: number; title: string; state?: string; isDraft?: boolean}
  repoOwner: string
  repoName: string
}) => {
  const getIconColor = () => {
    if (pr.state === 'merged') {
      return 'var(--fgColor-done)'
    }
    return 'var(--fgColor-open)'
  }

  const prUrl = `/${encodeURIComponent(repoOwner)}/${encodeURIComponent(repoName)}/pull/${encodeURIComponent(
    pr.number,
  )}`

  return (
    <a href={prUrl} target="_blank" rel="noopener noreferrer" className={styles.prLink}>
      <span style={{color: getIconColor()}}>
        <GitPullRequestIcon size={16} />
      </span>
      <span>#{pr.number}</span>
    </a>
  )
}

// Component for the second metadata row showing PR links and commit count
const CommitGroupMetadata = ({
  pr,
  commits,
  size,
  repoOwner,
  repoName,
}: {
  pr?: {number: number; title: string; state?: string; isDraft?: boolean}
  commits: Array<{
    oid: string
    author?: {
      login?: string
      date: string
    }
  }>
  size?: string
  repoOwner: string
  repoName: string
}) => {
  // Build dynamic URL for commit count link
  const buildCommitCountUrl = () => {
    if (commits.length === 1 && commits[0]) {
      // Single commit: direct link to commit page
      return commitPath({owner: repoOwner, repo: repoName, commitish: commits[0].oid})
    } else if (commits.length >= 2) {
      // Multiple commits: use compare view for exact precision
      // But only if we have valid dates to sort by
      const commitsWithValidDates = commits.filter(commit => commit.author?.date && commit.author.date.trim() !== '')

      if (commitsWithValidDates.length >= 2) {
        // Sort commits by date to get chronological order (earliest to latest)
        const sortedCommits = [...commitsWithValidDates].sort((a, b) => {
          const dateA = new Date(a.author!.date).getTime()
          const dateB = new Date(b.author!.date).getTime()
          return dateA - dateB
        })

        if (sortedCommits.length >= 2) {
          const earliestCommit = sortedCommits[0]
          const latestCommit = sortedCommits[sortedCommits.length - 1]

          if (earliestCommit && latestCommit) {
            // Use compare view from parent of earliest commit to latest commit
            // This shows exactly the commits in the group by starting from the parent
            return `/${encodeURIComponent(repoOwner)}/${encodeURIComponent(repoName)}/compare/${
              earliestCommit.oid
            }^...${latestCommit.oid}`
          }
        }
      }
    }

    // Fallback: return null to indicate no link should be created
    return null
  }

  const commitCountUrl = buildCommitCountUrl()
  const commitCount = commits.length

  return (
    <div className={styles.commitMetadataContainer}>
      <div className="d-flex flex-items-center">
        {commitCountUrl ? (
          <a href={commitCountUrl} target="_blank" rel="noopener noreferrer" className={styles.commitCountLink}>
            <GitCommitIcon size={16} />
            {commitCount === 1 ? '1 commit' : `${commitCount} commits`}
          </a>
        ) : (
          <span className={styles.commitCountText}>
            <GitCommitIcon size={16} />
            {commitCount === 1 ? '1 commit' : `${commitCount} commits`}
          </span>
        )}
        {size && <SizeBadge size={size} />}
      </div>
      {pr && <RichPullRequestLink pr={pr} repoOwner={repoOwner} repoName={repoName} />}
    </div>
  )
}

interface CopilotCommitRecapPanelProps {
  isVisible: boolean
  isLoading: boolean
  analysis: {analysisData: Array<{oid: string; summary: string; size: string}>; errorMessage: string}
  repoOwner: string
  repoName: string
  refName?: string
  onClose?: () => void
  commitGroups?: Array<{
    type: string
    pr?: {
      number: number
      title: string
      body?: string
      state?: string
      isDraft?: boolean
    }
    commits: Array<{
      oid: string
      message: string
      author?: {
        login?: string
        avatar_url?: string
        name: string
        email: string
        date: string
      }
    }>
  }>
}

const DEFAULT_COMMIT_GROUPS: CopilotCommitRecapPanelProps['commitGroups'] = []

export function CopilotCommitRecapPanel({
  isVisible,
  isLoading,
  analysis,
  repoOwner,
  repoName,
  refName,
  onClose,
  commitGroups = DEFAULT_COMMIT_GROUPS,
}: CopilotCommitRecapPanelProps) {
  const analysisBoxRef = useRef<HTMLDivElement>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  // Extract analysisData and errorMessage from the analysis prop
  const analysisData = analysis.analysisData
  const errorMessage = analysis.errorMessage

  // Create a map of commit OIDs to their analysis data (including size)
  const analysisDataMap = useMemo(() => {
    const map = new Map<string, {summary: string; size: string}>()
    for (const item of analysisData) {
      map.set(item.oid, {summary: item.summary, size: item.size})
    }
    return map
  }, [analysisData])

  // Create commit group sections with deterministic author display
  const commitGroupSections = useMemo(() => {
    if (!commitGroups || commitGroups.length === 0) return []

    return commitGroups.map((group, index) => {
      // Get unique authors from commits in this group
      const uniqueAuthors = new Map<string, {login?: string; avatarUrl?: string; name: string}>()

      for (const commit of group.commits) {
        if (commit.author) {
          const key = commit.author.login || commit.author.name
          if (!uniqueAuthors.has(key)) {
            uniqueAuthors.set(key, {
              login: commit.author.login,
              avatarUrl: commit.author.avatar_url,
              name: commit.author.name,
            })
          }
        }
      }

      return {
        groupIndex: index,
        authors: Array.from(uniqueAuthors.values()),
        commitCount: group.commits.length,
        commits: group.commits,
        pr: group.pr,
      }
    })
  }, [commitGroups])

  // Check if we have an error
  const isError = !!errorMessage

  const handleFeedback = useCallback(
    (feedback: 'positive' | 'negative') => {
      sendClickAnalyticsEvent({
        category: 'copilot.commit-recap',
        action: `feedback-${feedback}`,
        refName: refName || '',
      })
      setFeedbackSubmitted(true)
    },
    [sendClickAnalyticsEvent, refName],
  )

  const handleClose = useCallback(() => {
    sendClickAnalyticsEvent({
      category: 'copilot.commit-recap',
      action: 'panel-closed',
      refName: refName || '',
    })
    onClose?.()
  }, [onClose, sendClickAnalyticsEvent, refName])

  useEffect(() => {
    const container = mainContainerRef.current
    if (!container) {
      return
    }

    const handleClick = (event: Event) => {
      const mouseEvent = event as MouseEvent
      const target = mouseEvent.target as HTMLElement
      const link = target.closest('a')
      if (link && link.href) {
        sendClickAnalyticsEvent({
          category: 'copilot.commit-recap',
          action: 'link-clicked',
          refName: refName || '',
          href: link.href,
        })
      }
    }

    container.addEventListener('click', handleClick)
    return () => {
      container.removeEventListener('click', handleClick)
    }
  }, [isVisible, refName, analysisData, sendClickAnalyticsEvent])

  if (!isVisible) {
    return null
  }

  const showLoadingState = analysisData.length === 0 && !isError

  return (
    <div className={styles.Box_1} ref={mainContainerRef}>
      <div className={styles.Box_2}>
        <div className={styles.headerContainer}>
          <CopilotIcon className={styles.Octicon} />
          <Heading as="h3" className={styles.Heading}>
            Commit recap
          </Heading>
          <BetaLabel className="pl-2" />
        </div>
        {onClose && (
          <IconButton
            aria-label="Close commit recap"
            icon={XIcon}
            size="small"
            variant="invisible"
            onClick={handleClose}
          />
        )}
      </div>
      {showLoadingState && (
        <div className={styles.Box_3}>
          <div className={styles.Box_4}>
            <CopilotIconAnimation />
          </div>
          <span className={styles.Text}>Analyzing recent commits...</span>
        </div>
      )}
      {isError && (
        <Flash variant="danger" className={styles.Box_4}>
          {errorMessage}
        </Flash>
      )}
      {analysisData.length > 0 && !isError && (
        <>
          <div ref={analysisBoxRef} className={`${styles.Box_5} ${styles.fadeInAuthor}`}>
            {commitGroupSections.map((commitGroup, index) => {
              // Find the matching analysis data for this commit group using OID
              const groupOid = commitGroup.commits[0]?.oid
              const analysisItem = groupOid ? analysisDataMap.get(groupOid) : undefined
              const authorsToShow = commitGroup.authors
              // Get the latest commit date for the group
              const latestCommitDate = commitGroup.commits.reduce((latest, commit) => {
                const commitDate = new Date(commit.author?.date || 0)
                return commitDate > latest ? commitDate : latest
              }, new Date(0))

              // Skip this commit group if we don't have analysis for it
              if (!analysisItem) return null

              return (
                <div key={`commitGroup-${commitGroup.groupIndex}`} className={styles.Box_6}>
                  {/* Render author avatars/monograms deterministically */}
                  {authorsToShow.length > 0 && (
                    <div className={styles.Box_7}>
                      {authorsToShow.slice(0, 3).map((author, authorIndex) => (
                        <div
                          key={author.login || author.name}
                          className={`${styles.fadeInAuthor} ${
                            isLoading && index === commitGroupSections.length - 1 ? styles.isHidden : ''
                          }`}
                          style={{animationDelay: `${index * 200 + authorIndex * 100}ms`}}
                        >
                          <AuthorAvatar
                            login={author.login}
                            avatarUrl={author.avatarUrl}
                            name={author.name}
                            repoOwner={repoOwner}
                            repoName={repoName}
                            refName={refName}
                          />
                        </div>
                      ))}
                      {authorsToShow.length > 3 && <span className={styles.Text_1}>+{authorsToShow.length - 3}</span>}
                    </div>
                  )}

                  {/* Render AI-generated content with author names prefix */}
                  <div
                    className={`${styles.Box_8} ${styles.fadeInAuthor}`}
                    style={{animationDelay: `${index * 200}ms`}}
                  >
                    {/* Add author names as prefix if we have authors */}
                    {authorsToShow.length > 0 && (
                      <span>
                        {authorsToShow.map((author, idx) => (
                          <span key={author.login || author.name}>
                            <AuthorNameLink
                              author={author}
                              repoOwner={repoOwner}
                              repoName={repoName}
                              refName={refName}
                              className={styles.fadeInAuthor}
                            />
                            {idx < authorsToShow.length - 1 && ', '}
                          </span>
                        ))}
                        <RelativeTime
                          className={`${styles.fadeInAuthor} ${styles.relativeTime}`}
                          date={latestCommitDate}
                        />
                      </span>
                    )}
                    <MarkdownRenderer
                      markdown={analysisItem.summary}
                      isStreaming={false}
                      openLinksInCurrentTab={false}
                    />
                  </div>

                  {/* Render second row with PR link and commit count after the content */}
                  {commitGroup && (
                    <div
                      className={`${styles.fadeInAuthor} ${
                        isLoading && index === commitGroupSections.length - 1 ? styles.isHidden : ''
                      }`}
                      style={{animationDelay: `${index * 200 + 300}ms`}}
                    >
                      <CommitGroupMetadata
                        pr={commitGroup.pr}
                        commits={commitGroup.commits}
                        size={
                          commitGroup.commits.length > 0 && commitGroup.commits[0]?.oid
                            ? analysisDataMap.get(commitGroup.commits[0].oid)?.size
                            : undefined
                        }
                        repoOwner={repoOwner}
                        repoName={repoName}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
      {/* Show footer immediately when panel is visible */}
      <div className={styles.Footer}>
        <span className={styles.Text_2}>
          AI-generated content may be inaccurate. Please verify important information.
        </span>
        {!feedbackSubmitted ? (
          <div className={styles.FeedbackButtons}>
            <IconButton
              aria-label="Good summary"
              icon={ThumbsupIcon}
              size="small"
              variant="invisible"
              onClick={() => handleFeedback('positive')}
            />
            <IconButton
              aria-label="Bad summary"
              icon={ThumbsdownIcon}
              size="small"
              variant="invisible"
              onClick={() => handleFeedback('negative')}
            />
          </div>
        ) : (
          <span className={styles.Text_2}>
            Thank you. This feature is under development and <a href={GOOGLE_FORM_URL}>detailed feedback</a> is
            appreciated!
          </span>
        )}
      </div>
    </div>
  )
}
